import {
    useCallback, useMemo,
} from 'react'
import useHasPermission from 'app/hooks/useHasPermission'
import aPricingRateCardRequests from 'app/requests/APricingRateCard/APricingRateCard.requests'
import Button from 'app/shared-components/Button'
import {
    RateCardStatus,
} from 'app/enums/label.enums'
import useLabel from 'app/hooks/useLabel'
import useRatesRoutes from 'app/SkyNetRoutes/hooks/useRatesRoutes'
import {
    RoutesConfig,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'

import ARateCardForm from '../ARateCardForm'
import {
    ARateCardFormValue,
} from '../ARateCardForm/ARateCardForm.types'
import {
    isValidFromInPast,
} from '../ARateCards.utils'
import useChangeARateCardStatus from './hooks/useChangeARateCardStatus'

type Props = {
    onSuccess: (newRateCard: any) => void,
    data?: ARateCardFormValue,
}

export default function ARateCardUpdate({
    onSuccess,
    data,
}: Readonly<Props>) {
    const l = useLabel()
    const updatePermission = useHasPermission(['a_pricing_rates_update'])
    const copyPermission = useHasPermission(['a_pricing_rates_create'])

    const {
        ARateCards,
    } = useRatesRoutes()

    const {
        changeStatus,
    } = useChangeARateCardStatus({
        onSuccess,
    })

    const requestParams = useMemo(() => {
        return aPricingRateCardRequests.update({
            id: data?.id,
        })
    }, [data?.id])

    const handleCopy = useCallback(() => {
        ((ARateCards as RoutesConfig).All as RoutesConfig).Create.navigate({
            options: {
                state: {
                    copyFrom: {
                        id: data?.id,
                        account: data?.account,
                        comment: data?.comment,
                    },
                },
            },
        })
    }, [
        ARateCards,
        data,
    ])

    const handleStatusChange = useCallback((
        status: RateCardStatus,
        titleKey: string,
    ) => {
        changeStatus({
            id: data?.id,
            status,
            title: l(titleKey),
        })
    }, [
        changeStatus,
        data?.id,
        l,
    ])

    const getCustomButtons = useCallback(({
        isValid, edited,
    }: {
        isValid: boolean,
        edited: boolean,
    }) => {
        const buttons: JSX.Element[] = []
        const status = data?.status
        const validFromPast = isValidFromInPast(data?.validFrom)
        const disabled = !isValid || edited

        if (status === RateCardStatus.IN_PROGRESS && updatePermission) {
            buttons.push(
                <Button
                    key="set-to-valid"
                    label={l('SET_TO_VALID')}
                    onClick={() => {
                        handleStatusChange(RateCardStatus.VALID, 'RATE_CARD_STATUS_CHANGED_TO_VALID')
                    }}
                    disabled={disabled}
                />,
                <Button
                    key="set-to-cancelled"
                    label={l('SET_TO_CANCELLED')}
                    onClick={() => {
                        handleStatusChange(RateCardStatus.CANCELLED, 'RATE_CARD_STATUS_CHANGED_TO_CANCELLED')
                    }}
                    disabled={disabled}
                />,
            )
        }

        if (status === RateCardStatus.VALID && !validFromPast) {
            buttons.push(
                <Button
                    key="set-to-in-progress"
                    label={l('SET_TO_IN_PROGRESS')}
                    onClick={() => {
                        handleStatusChange(RateCardStatus.IN_PROGRESS, 'RATE_CARD_STATUS_CHANGED_TO_IN_PROGRESS')
                    }}
                    disabled={disabled}
                />,
            )
        }

        // Copy is allowed for: VALID with validFrom in past, or CANCELLED
        const canCopy = (status === RateCardStatus.VALID && validFromPast)
            || status === RateCardStatus.CANCELLED

        if (canCopy && copyPermission) {
            buttons.push(
                <Button
                    key="copy"
                    label={l('COPY')}
                    onClick={handleCopy}
                    disabled={disabled}
                />,
            )
        }

        return buttons
    }, [
        data?.status,
        data?.validFrom,
        updatePermission,
        copyPermission,
        l,
        handleStatusChange,
        handleCopy,
    ])

    return (
        <ARateCardForm
            disabled={!updatePermission}
            value={data}
            onSuccess={onSuccess}
            requestParams={requestParams}
            getCustomButtons={getCustomButtons}
        />
    )
}
